import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, Chip, Alert, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, TextField, IconButton } from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';
import { FREQUENCY_DIVISIONS } from '@/constants/feeInstallments';

export function InstallmentPreviewDialog({
  open,
  onClose,
  previewRows,
  setPreviewRows,
  isPreviewMatch,
  previewDiff,
  previewTotalSum,
  totalComponentAmount,
  freqKey,
  isCreating,
  handleSavePreview,
  handlePreviewRowChange,
  handleRemovePreviewRow,
  handleAddPreviewRow,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableRestoreFocus>
      <DialogTitle sx={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Preview & Modify Installment Schedule ({previewRows.length})</span>
        <Chip
          label={
            isPreviewMatch
              ? '100% Total Matched'
              : previewDiff > 0
                ? `₹${previewDiff.toLocaleString()} Remaining`
                : `₹${Math.abs(previewDiff).toLocaleString()} Excess`
          }
          color={isPreviewMatch ? 'success' : 'warning'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      </DialogTitle>
      <DialogContent dividers>
        {!isPreviewMatch && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Installment amounts sum to <strong>₹{previewTotalSum.toLocaleString()}</strong>, but component fee is{' '}
            <strong>₹{totalComponentAmount.toLocaleString()}</strong>. Total sum must equal ₹
            {totalComponentAmount.toLocaleString()} before saving.
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F8FAFC') }}>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 700, width: 50 }}>
                  #
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Installment Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, width: 140 }}>
                  Amount (₹)
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: 150 }}>Due Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, width: 100 }}>
                  Grace Days
                </TableCell>
                {!FREQUENCY_DIVISIONS[freqKey] && <TableCell align="center" sx={{ fontWeight: 700, width: 60 }}></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.map((row, idx) => (
                <TableRow key={row.tempId || idx}>
                  <TableCell align="center">
                    <Chip label={`#${idx + 1}`} size="small" variant="outlined" sx={{ fontWeight: 700, height: 22 }} />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={row.installment_name}
                      onChange={(e) => handlePreviewRowChange(idx, 'installment_name', e.target.value)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      slotProps={{ htmlInput: { style: { textAlign: 'right', fontWeight: 600 } } }}
                      value={row.amount}
                      onChange={(e) => handlePreviewRowChange(idx, 'amount', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="date"
                      fullWidth
                      value={row.due_date}
                      onChange={(e) => handlePreviewRowChange(idx, 'due_date', e.target.value)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="number"
                      sx={{ width: 80 }}
                      slotProps={{ htmlInput: { style: { textAlign: 'center' }, min: 0, max: 30 } }}
                      value={row.grace_days}
                      onChange={(e) => handlePreviewRowChange(idx, 'grace_days', e.target.value)}
                    />
                  </TableCell>
                  {!FREQUENCY_DIVISIONS[freqKey] && (
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleRemovePreviewRow(idx)}>
                        <Trash2 size={16} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          {!FREQUENCY_DIVISIONS[freqKey] ? (
            <Button size="small" variant="outlined" startIcon={<Plus size={16} />} onClick={handleAddPreviewRow}>
              Add Row
            </Button>
          ) : (
            <Box />
          )}

          <Typography variant="subtitle2" fontWeight={700} color={isPreviewMatch ? 'success.main' : 'warning.main'}>
            Total: ₹{previewTotalSum.toLocaleString()} / ₹{totalComponentAmount.toLocaleString()}
          </Typography>
        </Box>

        {!isPreviewMatch ? (
          <Alert
            severity={previewDiff > 0 ? 'warning' : 'error'}
            action={
              previewRows.length > 0 && (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setPreviewRows((prev) => {
                      const updated = [...prev];
                      const lastIdx = updated.length - 1;
                      const curAmount = Number(updated[lastIdx].amount) || 0;
                      updated[lastIdx] = {
                        ...updated[lastIdx],
                        amount: String(Number((curAmount + previewDiff).toFixed(2))),
                      };
                      return updated;
                    });
                  }}
                >
                  Auto-Balance ({previewDiff > 0 ? `+₹${previewDiff}` : `-₹${Math.abs(previewDiff)}`} to Last Row)
                </Button>
              )
            }
            sx={{ mt: 2, borderRadius: 1.5, alignItems: 'center' }}
          >
            <strong>Amount Mismatch:</strong> Scheduled total (<strong>₹{previewTotalSum.toLocaleString()}</strong>) is{' '}
            <strong>
              ₹{Math.abs(previewDiff).toLocaleString()} {previewDiff > 0 ? 'short of' : 'over'}
            </strong>{' '}
            the required Component Fee (<strong>₹{totalComponentAmount.toLocaleString()}</strong>).
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mt: 2, borderRadius: 1.5, py: 0.5 }}>
            <strong>Schedule Balanced:</strong> Installments sum to exactly ₹{totalComponentAmount.toLocaleString()}.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSavePreview}
          disabled={!isPreviewMatch || isCreating || previewRows.length === 0}
        >
          {isCreating ? 'Saving Schedule...' : 'Confirm & Save Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
